import { DestroyRef, Injectable, inject } from '@angular/core';
import { Observable, debounceTime, filter, map, share } from 'rxjs';
import { ApiBaseService } from './entities/api-base.service';

/** Módulo de las conexiones WebSocket de este proyecto (el mismo que usa el API al avisar). */
export const REALTIME_MODULE = 'MEDTRONIC-SURGICAL';

/** Aviso del API: qué módulos cambiaron. No trae datos: la pantalla vuelve a pedirlos. */
export interface RealtimeChange {
  entities: string[];   // en minúsculas: 'interventions', 'stock', 'materialrequests'…
  action: string;       // 'POST', 'PATCH', 'POST/supply'…
  by: string | null;    // usuario que hizo el cambio
  at: string;
}

/**
 * Conexión única por sesión al WebSocket de Epione (ticket → wss, heartbeat
 * cada 5 min y reconexión a las 2 h, ver ApiBaseService). La abre el layout al
 * iniciar sesión y la cierra al salir.
 */
@Injectable({ providedIn: 'root' })
export class RealtimeService extends ApiBaseService<any> {
  private orgId: string | null = null;

  readonly changes$: Observable<RealtimeChange> = this.wsMessages$.pipe(
    filter(m => m?.event === 'changed' && !!m.data),
    map(m => {
      const raw = Array.isArray(m.data.entities) ? m.data.entities : [m.data.entity];
      return {
        entities: raw.filter(Boolean).map((e: unknown) => String(e).toLowerCase()),
        action: String(m.data.action ?? ''),
        by: m.data.by ?? null,
        at: String(m.data.at ?? ''),
      };
    }),
    share(),
  );

  constructor() { super('realtime'); }

  /** Abre (o mantiene) la conexión para la organización del usuario. */
  start(orgId: string | null | undefined): void {
    if (!orgId || this.orgId === orgId) return;
    this.orgId = orgId;
    void this.wsConnect({ module: REALTIME_MODULE, org_id: orgId });
  }

  stop(): void {
    this.orgId = null;
    this.wsDisconnect();
  }

  /** Cambios de esos módulos hechos por OTRO usuario (los propios ya los recarga la pantalla). */
  on(entities: string[] | (() => string[])): Observable<RealtimeChange> {
    return this.changes$.pipe(filter(c => {
      if (c.by && c.by === sessionStorage.getItem('userId')) return false;
      const wanted = (typeof entities === 'function' ? entities() : entities).map(e => e.toLowerCase());
      return c.entities.some(e => wanted.includes(e));
    }));
  }
}

/**
 * Recarga la pantalla cuando otro usuario cambia alguno de esos módulos.
 * Se usa como campo del componente (contexto de inyección) y se limpia sola:
 *
 *   private readonly live = liveReload(['custody'], () => this.pageLoad.ready && this.pageLoad.refresh(this.load()));
 *
 * - Junta varios avisos seguidos en una sola recarga.
 * - Si la pestaña está oculta, recarga al volver a ella.
 */
export function liveReload(entities: string[] | (() => string[]), reload: () => unknown, debounceMs = 1500): void {
  const realtime = inject(RealtimeService);
  const destroyRef = inject(DestroyRef);
  let pending = false;

  const run = () => {
    pending = false;
    try {
      const r: any = reload();
      if (r && typeof r.catch === 'function') r.catch(() => undefined);
    } catch { /* la pantalla muestra sus propios errores */ }
  };
  const onVisible = () => { if (pending && document.visibilityState === 'visible') run(); };
  document.addEventListener('visibilitychange', onVisible);

  const sub = realtime.on(entities).pipe(debounceTime(debounceMs)).subscribe(() => {
    if (document.visibilityState === 'hidden') pending = true;
    else run();
  });
  destroyRef.onDestroy(() => {
    sub.unsubscribe();
    document.removeEventListener('visibilitychange', onVisible);
  });
}
